import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Alert, TextField } from '@mui/material';
import { cloneDeep } from 'lodash-es';
import axios from 'axios';

import Modal from 'molecules/Modal';
import {
  ModalActions,
  ModalContent,
  ModalSubmitButton,
} from 'molecules/Modal/Modal.styles';

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

type FormState = {
  subject: { value: string; error: string };
  message: { value: string; error: string };
};

const initialForm: FormState = {
  subject: { value: '', error: '' },
  message: { value: '', error: '' },
};

const SupportModal = ({ open, onClose }: SupportModalProps) => {
  const [form, setForm] = useState<FormState>(cloneDeep(initialForm));
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof FormState
  ) => {
    const updated = cloneDeep(form);
    updated[field].value = e.target.value;
    if (e.target.value) updated[field].error = '';
    setForm(updated);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const updated = cloneDeep(form);
    let hasError = false;
    if (!updated.subject.value.trim()) {
      updated.subject.error = 'Please enter a subject.';
      hasError = true;
    }
    if (!updated.message.value.trim()) {
      updated.message.error = 'Please describe your request.';
      hasError = true;
    }
    if (hasError) {
      setForm(updated);
      return;
    }

    const payload = {
      subject: form.subject.value,
      message: form.message.value,
    };

    try {
      setLoading(true);
      setApiError('');
      await axios.post(
        `${process.env.REACT_APP_USER_SERVICE}/support-contact`,
        payload
      );
      setSubmitted(true);
    } catch {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(cloneDeep(initialForm));
    setSubmitted(false);
    setApiError('');
    onClose();
  };

  return (
    <Modal open={open} maxWidth="sm" title="Contact us" onClose={handleClose}>
      <form onSubmit={handleSubmit} noValidate>
        <ModalContent
          sx={{
            '.MuiPaper-root': {
              width: 'auto !important',
              minWidth: '200px !important',
            },
          }}
        >
          {submitted && (
            <Alert
              severity="success"
              sx={{
                mb: 1,
                width: 'auto',
                minWidth: '200px',
                '& .MuiAlert-message': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              }}
            >
              Your request has been sent. We&apos;ll get back to you shortly.
            </Alert>
          )}
          {apiError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {apiError}
            </Alert>
          )}
          <TextField
            disabled={submitted}
            error={!!form.subject.error}
            helperText={form.subject.error}
            fullWidth
            size="small"
            variant="outlined"
            label="Subject"
            value={form.subject.value}
            onChange={(e) => handleChange(e, 'subject')}
            InputLabelProps={{ shrink: true }}
            sx={{ '.MuiInputBase-input': { fontSize: '0.875rem' }, mt: 1 }}
          />
          <TextField
            disabled={submitted}
            error={!!form.message.error}
            helperText={form.message.error}
            fullWidth
            multiline
            rows={6}
            size="small"
            variant="outlined"
            label="Describe your request"
            value={form.message.value}
            onChange={(e) => handleChange(e, 'message')}
            InputLabelProps={{ shrink: true }}
            sx={{
              '.MuiInputBase-input': { fontSize: '0.875rem' },
              mt: 1,
              mb: 2,
            }}
          />
        </ModalContent>
        <ModalActions>
          <ModalSubmitButton
            variant="contained"
            type="submit"
            loading={loading}
            disabled={submitted}
          >
            Send
          </ModalSubmitButton>
        </ModalActions>
      </form>
    </Modal>
  );
};

export default SupportModal;
